import { useState } from 'react';
import type { AltYApartment } from '../types/global';

type ApartmentListProps = {
  apartments: AltYApartment[];
  // Delete an apartment by id. The parent reconciles after the broker confirms
  // (optimistic remove there).
  onDelete: (id: string) => void;
  // Close affordance. On desktop this flips back to the OneSheet iframe; on the
  // mobile overlay it drops back to the conversation. Omitted → no close button.
  onClose?: () => void;
};

function formatPrice(price: AltYApartment['price']): string | null {
  if (price == null || price === '') return null;
  if (typeof price === 'number') return `€${price.toLocaleString('nl-NL')}`;
  const trimmed = String(price).trim();
  if (!trimmed) return null;
  return /[€$£]/.test(trimmed) ? trimmed : `€${trimmed}`;
}

// One contact line in the popup — a labelled link or plain value, rendered only
// when the field is present.
function ContactRow({ icon, label, href, value }: {
  icon: string; label: string; href?: string; value?: string;
}) {
  const text = value || href;
  if (!text) return null;
  return (
    <li className="alty-apt-contact-row">
      <i className={`fas ${icon}`} aria-hidden="true" />
      <span className="alty-apt-contact-label">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer">{text}</a>
      ) : (
        <span>{text}</span>
      )}
    </li>
  );
}

// Full-detail popup for a single apartment: photo gallery + every captured
// field. Mounted over the list; its own close button (and the backdrop) dismiss
// it back to the cards.
function ApartmentDetail({ apt, onClose }: { apt: AltYApartment; onClose: () => void }) {
  const photos = apt.photos?.filter(Boolean) ?? [];
  const [photoIdx, setPhotoIdx] = useState(0);
  const idx = photos.length ? ((photoIdx % photos.length) + photos.length) % photos.length : 0;
  const price = formatPrice(apt.price);
  const contact = apt.contact || {};
  return (
    <div className="alty-apt-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="alty-apt-modal" onClick={(e) => e.stopPropagation()}>
        <header className="alty-apt-modal-head">
          <span className="alty-apt-modal-title">{apt.title || apt.address || 'Apartment'}</span>
          <button type="button" className="alty-icon-button" title="Close" aria-label="Close" onClick={onClose}>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </header>
        <div className="alty-apt-modal-body">
          {photos.length ? (
            <div className="alty-apt-gallery">
              <img src={photos[idx]} alt={`Photo ${idx + 1} of ${apt.title || 'apartment'}`} />
              {photos.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="alty-apt-gallery-nav prev"
                    aria-label="Previous photo"
                    onClick={() => setPhotoIdx((p) => p - 1)}
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="alty-apt-gallery-nav next"
                    aria-label="Next photo"
                    onClick={() => setPhotoIdx((p) => p + 1)}
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                  </button>
                  <span className="alty-apt-gallery-count">{idx + 1} / {photos.length}</span>
                </>
              ) : null}
            </div>
          ) : (
            <div className="alty-apt-gallery alty-apt-gallery-empty">
              <i className="fas fa-images" aria-hidden="true" />
            </div>
          )}

          <div className="alty-apt-facts">
            {price ? <span className="alty-apt-fact price"><i className="fas fa-euro-sign" aria-hidden="true" />{price}<span className="alty-apt-permo">/mo</span></span> : null}
            {apt.postedAt ? <span className="alty-apt-fact"><i className="fas fa-clock" aria-hidden="true" />{apt.postedAt}</span> : null}
            {apt.originFound === false ? <span className="alty-apt-fact warn">origin unknown</span> : null}
            {apt.isRepost ? <span className="alty-apt-fact">repost</span> : null}
          </div>

          {apt.address || apt.city ? (
            <p className="alty-apt-address">
              <i className="fas fa-map-marker-alt" aria-hidden="true" />
              {[apt.address, apt.city].filter(Boolean).join(', ')}
              {apt.mapsUrl ? (
                <a href={apt.mapsUrl} target="_blank" rel="noopener noreferrer" className="alty-apt-maps-link">Google Maps</a>
              ) : null}
            </p>
          ) : null}

          {apt.travel ? <p className="alty-apt-travel">{apt.travel}</p> : null}
          {apt.details ? <p className="alty-apt-details">{apt.details}</p> : null}

          <div className="alty-apt-contact">
            <h4>Contact {contact.agentName ? `· ${contact.agentName}` : ''}</h4>
            <ul>
              <ContactRow icon="fa-external-link-alt" label="Listing" href={apt.sourceUrl} value={apt.sourceName || apt.sourceUrl} />
              <ContactRow icon="fa-link" label="Agent page" href={contact.agentPage} />
              <ContactRow icon="fa-phone" label="Phone" value={contact.phone} href={contact.phone ? `tel:${contact.phone}` : undefined} />
              <ContactRow icon="fa-envelope" label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : undefined} />
              <ContactRow icon="fa-external-link-alt" label="Form" href={contact.form} />
              <ContactRow icon="fa-phone" label="WhatsApp" value={contact.whatsapp} href={contact.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/[^\d]/g, '')}` : undefined} />
              <ContactRow icon="fa-building" label="Office" href={contact.officePage} />
            </ul>
            {contact.notes ? <p className="alty-apt-contact-notes">{contact.notes}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// The apartment finder's saved result cards — the right-pane (desktop) /
// full-screen overlay (mobile) view the header's buildings toggle flips to.
// Cards arrive already sorted newest-first from the broker. Read-only apart from
// per-card delete; clicking a card opens the detail popup. New cards are saved
// from the agent's reply (see App.tsx), not added here.
export function ApartmentList({ apartments, onDelete, onClose }: ApartmentListProps) {
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = detailId ? apartments.find((a) => a.id === detailId) ?? null : null;
  return (
    <div className="alty-apt-list">
      <header className="alty-apt-list-head">
        <span className="alty-apt-list-title">
          <i className="fas fa-building" aria-hidden="true" />
          <span>Apartments</span>
          {apartments.length ? <span className="alty-apt-count">{apartments.length}</span> : null}
        </span>
        {onClose ? (
          <button type="button" className="alty-icon-button" title="Back" aria-label="Back" onClick={onClose}>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        ) : null}
      </header>
      {apartments.length === 0 ? (
        <div className="alty-apt-empty">
          <i className="fas fa-building" aria-hidden="true" />
          <p>No apartments yet.</p>
          <p className="alty-apt-empty-hint">Ask the agent to search and the verified results land here, newest first.</p>
        </div>
      ) : (
        <ul className="alty-apt-cards">
          {apartments.map((apt) => {
            const price = formatPrice(apt.price);
            const thumb = apt.photos?.find(Boolean);
            return (
              <li key={apt.id} className="alty-apt-card">
                <button
                  type="button"
                  className="alty-apt-card-main"
                  onClick={() => setDetailId(apt.id)}
                  title="View details"
                >
                  <span className="alty-apt-thumb">
                    {thumb ? (
                      <img src={thumb} alt="" loading="lazy" />
                    ) : (
                      <i className="fas fa-building" aria-hidden="true" />
                    )}
                  </span>
                  <span className="alty-apt-card-body">
                    <span className="alty-apt-card-title">{apt.title || apt.address || 'Apartment'}</span>
                    <span className="alty-apt-card-sub">
                      {apt.city ? <span><i className="fas fa-map-marker-alt" aria-hidden="true" />{apt.city}</span> : null}
                      {price ? <span className="alty-apt-card-price">{price}<span className="alty-apt-permo">/mo</span></span> : null}
                    </span>
                    <span className="alty-apt-card-meta">
                      {apt.postedAt ? <span><i className="fas fa-clock" aria-hidden="true" />{apt.postedAt}</span> : null}
                      {apt.sourceName ? <span className="alty-apt-card-source">{apt.sourceName}</span> : null}
                      {apt.originFound === false ? <span className="alty-apt-card-warn">origin unknown</span> : null}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="alty-apt-delete"
                  title="Delete apartment"
                  aria-label="Delete apartment"
                  onClick={() => onDelete(apt.id)}
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {detail ? <ApartmentDetail apt={detail} onClose={() => setDetailId(null)} /> : null}
    </div>
  );
}
